import { motion } from "framer-motion";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { MapPin, FileText, Users, TrendingUp } from "lucide-react";

const counties = [
  { name: "Jefferson", x: 168, y: 195, bills: 12, status: "high", population: "674K" },
  { name: "Mobile", x: 132, y: 380, bills: 8, status: "high", population: "414K" },
  { name: "Montgomery", x: 230, y: 290, bills: 15, status: "high", population: "229K" },
  { name: "Madison", x: 195, y: 65, bills: 10, status: "high", population: "395K" },
  { name: "Tuscaloosa", x: 138, y: 210, bills: 6, status: "medium", population: "227K" },
  { name: "Baldwin", x: 165, y: 395, bills: 4, status: "medium", population: "231K" },
  { name: "Lee", x: 305, y: 260, bills: 5, status: "medium", population: "174K" },
  { name: "Morgan", x: 170, y: 95, bills: 3, status: "low", population: "123K" },
  { name: "Etowah", x: 230, y: 130, bills: 2, status: "low", population: "103K" },
  { name: "Houston", x: 310, y: 370, bills: 4, status: "medium", population: "106K" },
  { name: "Shelby", x: 200, y: 220, bills: 3, status: "low", population: "223K" },
  { name: "Calhoun", x: 265, y: 170, bills: 2, status: "low", population: "116K" },
  { name: "Marshall", x: 220, y: 100, bills: 1, status: "low", population: "97K" },
  { name: "Lauderdale", x: 125, y: 50, bills: 3, status: "low", population: "93K" },
  { name: "Talladega", x: 240, y: 200, bills: 2, status: "low", population: "82K" },
  { name: "Dale", x: 290, y: 340, bills: 1, status: "low", population: "49K" },
  { name: "Dallas", x: 175, y: 290, bills: 5, status: "medium", population: "38K" },
  { name: "Hale", x: 140, y: 260, bills: 2, status: "low", population: "15K" },
];

const recentBills = [
  { id: "AL-HB221", title: "Birmingham Affordable Housing Trust Fund", county: "Jefferson", area: "Housing", status: "In Committee" },
  { id: "AL-SB118", title: "Montgomery Transit Equity Act", county: "Montgomery", area: "Housing", status: "Passed Senate" },
  { id: "AL-HB445", title: "Black Belt Clean Water Access Act", county: "Dallas", area: "Climate", status: "Introduced" },
  { id: "AL-SB302", title: "Huntsville Youth STEM Initiative", county: "Madison", area: "Education", status: "In Committee" },
  { id: "AL-HB189", title: "Mobile Bay Environmental Protection", county: "Mobile", area: "Climate", status: "Floor Vote" },
  { id: "AL-SB067", title: "Sentencing Reform Pilot Program", county: "Montgomery", area: "Justice", status: "Passed Senate" },
];

const statusColor = (status: string) => {
  switch (status) {
    case "high": return "hsl(var(--primary))";
    case "medium": return "hsl(var(--accent))";
    default: return "hsl(var(--muted-foreground))";
  }
};

const areaColor = (area: string) => {
  switch (area) {
    case "Housing": return "bg-accent/10 text-accent border-accent/20";
    case "Climate": return "bg-issue-climate/10 text-issue-climate border-issue-climate/20";
    case "Education": return "bg-issue-education/10 text-issue-education border-issue-education/20";
    case "Justice": return "bg-issue-justice/10 text-issue-justice border-issue-justice/20";
    default: return "bg-muted text-muted-foreground";
  }
};

const AlabamaMap = () => {
  const [selected, setSelected] = useState<string | null>(null);
  const selectedCounty = counties.find((c) => c.name === selected);

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <span className="text-primary text-sm font-semibold tracking-wider uppercase">
            Alabama Focus
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 mb-4">
            Legislation Activity Map
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl">
            Explore active bills and advocacy efforts across Alabama's counties.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="lg:col-span-3 min-w-0 bg-card border border-border rounded-lg p-6 relative min-h-[420px]"
          >
            {/* Alabama outline SVG */}
            <svg viewBox="0 0 400 450" className="w-full h-full" style={{ maxHeight: 450 }}>
              {/* Simplified Alabama outline */}
              <path
                d="M80,30 L320,30 L325,35 L330,60 L325,80 L330,100 L325,130 L330,160 L325,190 L330,220 L325,250 L330,280 L325,310 L330,340 L325,370 L320,390 L310,400 L280,410 L200,420 L160,415 L130,400 L100,410 L90,395 L95,370 L90,340 L85,310 L90,280 L85,250 L90,220 L85,190 L90,160 L85,130 L90,100 L85,70 L80,30Z"
                fill="hsl(220 16% 16%)"
                stroke="hsl(215 12% 30%)"
                strokeWidth="1.5"
              />

              {/* County dots */}
              {counties.map((county) => (
                <g key={county.name} onClick={() => setSelected(county.name)} className="cursor-pointer">
                  <motion.circle
                    cx={county.x}
                    cy={county.y}
                    r={selected === county.name ? 14 : Math.max(6, county.bills * 1.2)}
                    fill={statusColor(county.status)}
                    fillOpacity={selected === county.name ? 0.4 : 0.25}
                    initial={false}
                    animate={{ r: selected === county.name ? 14 : Math.max(6, county.bills * 1.2) }}
                  />
                  <motion.circle
                    cx={county.x}
                    cy={county.y}
                    r={selected === county.name ? 7 : 4}
                    fill={statusColor(county.status)}
                    initial={false}
                    animate={{ r: selected === county.name ? 7 : 4 }}
                  />
                  {(selected === county.name || county.status === "high") && (
                    <text
                      x={county.x}
                      y={county.y - (selected === county.name ? 20 : 14)}
                      textAnchor="middle"
                      fill="hsl(210 20% 92%)"
                      fontSize="11"
                      fontWeight="600"
                      fontFamily="Space Grotesk, sans-serif"
                    >
                      {county.name}
                    </text>
                  )}
                </g>
              ))}
            </svg>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-primary" /> High activity
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-accent" /> Medium
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground" /> Low
              </span>
            </div>
          </motion.div>

          {/* Sidebar */}
          <div className="lg:col-span-2 min-w-0 flex flex-col gap-4">
            {/* County detail card */}
            <motion.div
              layout
              className="bg-card border border-border rounded-lg p-6"
            >
              {selectedCounty ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl font-bold flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      {selectedCounty.name} County
                    </h3>
                    <button
                      onClick={() => setSelected(null)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-secondary/50 rounded-md p-3 text-center">
                      <FileText className="h-4 w-4 mx-auto mb-1 text-primary" />
                      <div className="text-lg font-bold">{selectedCounty.bills}</div>
                      <div className="text-[10px] text-muted-foreground uppercase">Bills</div>
                    </div>
                    <div className="bg-secondary/50 rounded-md p-3 text-center">
                      <Users className="h-4 w-4 mx-auto mb-1 text-accent" />
                      <div className="text-lg font-bold">{selectedCounty.population}</div>
                      <div className="text-[10px] text-muted-foreground uppercase">Pop.</div>
                    </div>
                    <div className="bg-secondary/50 rounded-md p-3 text-center">
                      <TrendingUp className="h-4 w-4 mx-auto mb-1 text-issue-climate" />
                      <div className="text-lg font-bold capitalize">{selectedCounty.status}</div>
                      <div className="text-[10px] text-muted-foreground uppercase">Activity</div>
                    </div>
                  </div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Related Bills
                  </h4>
                  <div className="space-y-2">
                    {recentBills
                      .filter((b) => b.county === selectedCounty.name)
                      .map((bill) => (
                        <div key={bill.id} className="bg-secondary/30 rounded-md px-3 py-2 text-sm">
                          <div className="font-mono text-xs text-muted-foreground">{bill.id}</div>
                          <div className="font-medium text-foreground text-xs mt-0.5">{bill.title}</div>
                        </div>
                      ))}
                    {recentBills.filter((b) => b.county === selectedCounty.name).length === 0 && (
                      <p className="text-xs text-muted-foreground">No tracked bills for this county.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <MapPin className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    Click a county on the map to view details
                  </p>
                </div>
              )}
            </motion.div>

            {/* Recent bills list */}
            <div className="bg-card border border-border rounded-lg p-6 flex-1">
              <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Recent Alabama Bills
              </h3>
              <div className="space-y-3">
                {recentBills.map((bill) => (
                  <motion.div
                    key={bill.id}
                    whileHover={{ x: 4 }}
                    className="flex items-start gap-3 cursor-pointer group"
                    onClick={() => setSelected(bill.county)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-xs text-muted-foreground">{bill.id}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${areaColor(bill.area)}`}>
                          {bill.area}
                        </Badge>
                      </div>
                      <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors truncate">
                        {bill.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{bill.county} County · {bill.status}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlabamaMap;
